import { useEffect, useState } from "react"
import { useParams } from "react-router"
import type { GuidebookData } from "./types"
import { fetchGuidebook } from "./api"
import { GuidebookShell } from "./GuidebookShell"
import { ConciergeShell } from "./concierge/ConciergeShell"
import { ReserveShell } from "./reserve/ReserveShell"
import "./guidebook.css"

export function GuidebookPage() {
  const { key } = useParams<{ key: string }>()
  const [data, setData] = useState<GuidebookData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!key) {
      setError("No guidebook key provided.")
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    fetchGuidebook(key)
      .then((result) => {
        if (cancelled) return
        if (!result.ok) {
          setError(result.error || "Failed to load guidebook.")
          return
        }
        setData(result)
        if (result.property?.name) {
          document.title = `${result.property.name} — Guidebook`
        }
      })
      .catch((err) => {
        if (cancelled) return
        setError(err.message || "Something went wrong.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [key])

  if (loading) {
    return (
      <div className="guidebook">
        <div className="gb-skeleton gb-skeleton--hero" />
        <div className="gb-skeleton gb-skeleton--card" />
        <div className="gb-skeleton gb-skeleton--card" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="guidebook">
        <div className="guidebook__error">
          <h1 className="guidebook__error-title">Guidebook unavailable</h1>
          <p className="guidebook__error-message">{error}</p>
          <button
            className="guidebook__error-retry"
            onClick={() => {
              setLoading(true)
              setError(null)
              if (key) {
                fetchGuidebook(key)
                  .then((result) => {
                    if (!result.ok) { setError(result.error || "Failed to load."); return }
                    setData(result)
                    if (result.property?.name) document.title = `${result.property.name} — Guidebook`
                  })
                  .catch((err) => setError(err.message || "Something went wrong."))
                  .finally(() => setLoading(false))
              }
            }}
            type="button"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (!data) return null

  if (data.template === "concierge") {
    return <ConciergeShell data={data} />
  }

  if (data.template === "reserve") {
    return <ReserveShell data={data} />
  }

  return (
    <div className="guidebook">
      <GuidebookShell data={data} />
    </div>
  )
}
